/**
 * Magic Login handler.
 *
 * Landing page for admin-issued one-time sign-in links.
 * URL: /magic-login?token=ml_<...>
 *
 * Flow:
 *   1. Read `token` from URL.
 *   2. POST /api/auth/magic-login  →  server validates, marks used, creates
 *      a 7-day session cookie, returns the user.
 *   3. Call AuthContext.checkAuth() to hydrate the app with the new session.
 *   4. Redirect to /dashboard on success, or show a clear error on failure.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, ShieldAlert, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MagicLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('working'); // working | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setErrorMsg('This sign-in link is missing a token. Please ask support to send you a new link.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/magic-login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (cancelled) return;

        if (!res.ok) {
          let msg = 'This sign-in link could not be used.';
          try {
            const data = await res.json();
            if (data?.detail) msg = data.detail;
          } catch { /* ignore parse errors */ }
          setErrorMsg(msg);
          setStatus('error');
          return;
        }

        // Session cookie is now set. Hydrate AuthContext and go to dashboard.
        await checkAuth();
        if (!cancelled) navigate('/dashboard', { replace: true });
      } catch (e) {
        if (cancelled) return;
        setErrorMsg('Network error — please check your connection and try the link again.');
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (status === 'working') {
    return (
      <div
        data-testid="magic-login-loading"
        className="min-h-screen bg-background flex items-center justify-center px-6"
      >
        <div className="flex flex-col items-center text-center max-w-md">
          <Loader2 className="h-8 w-8 text-emerald-400 animate-spin mb-4" />
          <h1 className="text-white text-lg font-semibold mb-1">Signing you in…</h1>
          <p className="text-muted-foreground text-sm">
            One moment while we verify your sign-in link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="magic-login-error"
      className="min-h-screen bg-background flex items-center justify-center px-6"
    >
      <div className="max-w-md w-full bg-foreground border border-white/10 rounded-2xl p-8 text-center">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-red-500/10 border border-red-500/30 mb-4">
          <ShieldAlert className="h-6 w-6 text-red-400" />
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">Sign-in link problem</h1>
        <p className="text-muted-foreground text-sm mb-6" data-testid="magic-login-error-message">
          {errorMsg}
        </p>
        <Link
          to="/login"
          data-testid="magic-login-back-to-signin"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-medium px-5 py-2.5 rounded-lg transition"
        >
          Back to sign-in <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
